package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Shared: topic, date and reading time ----------

val BlogMetaStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).color(RdColor.Muted)
            .css("flex-wrap" to "wrap", "gap" to "6px 14px", "font" to "500 .72rem/1.4 ${RdFont.MONO_STACK}", "letter-spacing" to ".06em", "text-transform" to "uppercase")
    }
    cssRule(" span + span::before") { Modifier.color(RdColor.LogoBlue).css("content" to "\"·\"", "margin-right" to "14px") }
}

// ---------- Hero: topics and the latest guide ----------

val BlogTopicsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "margin" to "0", "padding" to "0") }
    cssRule(" li") {
        Modifier.color(RdColor.Brand).backgroundColor(RdColor.SkySoft).border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(999.px)
            .css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase", "padding" to "8px 11px")
    }
}

/** The whole card is one link to the article. */
val BlogLatestStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Paper).color(RdColor.Ink).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(20.px).textDecorationLine(TextDecorationLine.None)
            .css("overflow" to "hidden", "min-width" to "0", "box-shadow" to RdSize.SHADOW)
    }
    cssRule(":hover img") { Modifier.css("transform" to "scale(1.03)") }
    cssRule(":hover h2") { Modifier.color(RdColor.Brand) }
}

val BlogLatestFigureStyle = CssStyle {
    base { Modifier.position(Position.Relative).css("margin" to "0", "aspect-ratio" to "16 / 9", "max-width" to "100%", "overflow" to "hidden") }
    cssRule(" img") {
        Modifier.display(DisplayStyle.Block)
            .css("width" to "100%", "height" to "100%", "object-fit" to "cover", "transition" to "transform .5s ease")
    }
    cssRule(" span") { Modifier.position(Position.Absolute).css("top" to "14px", "left" to "14px") }
}

val BlogLatestBodyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(10.px).css("padding" to "20px 22px 22px") }
    cssRule(" h2") {
        Modifier.fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "clamp(1.3rem, 1rem + 1vw, 1.7rem)", "font-weight" to 800, "font-stretch" to "108%", "line-height" to 1.15, "margin" to "0")
    }
    cssRule(" > p:not(:first-child)") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem") }
}

val BlogReadStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font-weight" to 600, "font-size" to ".95rem")
}

// ---------- Guides: a numbered reading path ----------

val BlogPathStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(20.px).css("list-style" to "none", "margin" to "0", "padding" to "0", "counter-reset" to "guide")
}

/** Number, picture and text; the number comes from the list counter so the order is set by the data. */
val BlogGuideStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(20.px)
            .css("grid-template-columns" to "1fr", "gap" to "18px", "align-items" to "start", "padding" to "18px")
    }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "56px 1fr", "gap" to "18px 28px", "padding" to "24px") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "72px 300px 1fr", "gap" to "32px") }
    cssRule("::before") {
        Modifier.color(RdColor.LogoBlue).fontFamily(*RdFont.DISPLAY)
            .css(
                "counter-increment" to "guide", "content" to "counter(guide, decimal-leading-zero)",
                "font-size" to "clamp(2.2rem, 1.6rem + 1.6vw, 3rem)", "font-weight" to 900, "font-stretch" to "118%", "line-height" to 1,
            )
    }
}

val BlogGuideFigureStyle = CssStyle {
    base { Modifier.borderRadius(14.px).css("margin" to "0", "overflow" to "hidden", "aspect-ratio" to "3 / 2", "max-width" to "100%") }
    Breakpoint.MD { Modifier.css("grid-column" to "2") }
    Breakpoint.LG { Modifier.css("grid-column" to "auto") }
    cssRule(" a") { Modifier.display(DisplayStyle.Block).css("height" to "100%") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "100%", "object-fit" to "cover", "transition" to "transform .5s ease") }
    cssRule(" a:hover img") { Modifier.css("transform" to "scale(1.03)") }
}

val BlogGuideBodyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("min-width" to "0") }
    Breakpoint.MD { Modifier.css("grid-column" to "2") }
    Breakpoint.LG { Modifier.css("grid-column" to "auto") }
    cssRule(" h3") {
        Modifier.fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "clamp(1.25rem, 1rem + .8vw, 1.55rem)", "font-weight" to 800, "font-stretch" to "108%", "line-height" to 1.2, "margin" to "0")
    }
    cssRule(" h3 a") { Modifier.color(RdColor.Ink).textDecorationLine(TextDecorationLine.None) }
    cssRule(" h3 a:hover") { Modifier.color(RdColor.Brand) }
    cssRule(" > p:not(:first-child)") { Modifier.color(RdColor.Muted).css("max-width" to "62ch") }
    cssRule(" > a") { Modifier.css("justify-self" to "start", "padding" to "12px 18px", "font-size" to ".95rem") }
}

val BlogInsideStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("border-top" to "1px solid rgb(220,227,236)", "padding-top" to "12px") }
    cssRule(" p") {
        Modifier.color(RdColor.Muted)
            .css("font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase", "margin" to "0")
    }
    cssRule(" ul") { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "margin" to "0", "padding" to "0") }
    cssRule(" li") {
        Modifier.backgroundColor(RdColor.Ground).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px)
            .css("font-size" to ".86rem", "padding" to "6px 11px")
    }
}
